import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Calculator, Calendar, History, AlertTriangle } from 'lucide-react';

interface MembershipWizardProps {
    memberId?: string; // Optional - if not provided, will show member selection
    onSuccess: () => void;
    onCancel: () => void;
    mode?: 'membership' | 'purchase'; // 'membership' uses /api/members/:id/memberships, 'purchase' uses /api/purchases
}

interface Plan {
    id: string;
    name: string;
    description: string | null;
    price: number | null;
    durationMonths: number | null;
    features: any;
    isActive: boolean;
}

type WizardStep = 'plan' | 'dates' | 'payment-summary' | 'payment-method' | 'confirmation';

export default function MembershipWizard({ memberId, onSuccess, onCancel, mode = 'membership' }: MembershipWizardProps) {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState<WizardStep>('plan');
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(true);

    // Form data
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [discountType, setDiscountType] = useState<'none' | 'amount' | 'percent'>('none');
    const [discountValue, setDiscountValue] = useState('');
    const [taxPercent, setTaxPercent] = useState('18');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentNotes, setPaymentNotes] = useState('');
    // Partial payment fields
    const [paidNow, setPaidNow] = useState<string>('');
    const [dueDate, setDueDate] = useState<string>('');

    // Calculated values
    const [membershipId, setMembershipId] = useState<string | null>(null);
    const [paymentSummary, setPaymentSummary] = useState<any>(null);
    const [existingMemberships, setExistingMemberships] = useState<any[]>([]);
    const [minStartDate, setMinStartDate] = useState<string>('');
    // Track the final payment result for confirmation screen
    const [finalPaymentStatus, setFinalPaymentStatus] = useState<string>('paid');
    const [finalPendingAmount, setFinalPendingAmount] = useState<number>(0);

    useEffect(() => {
        fetchPlans();
        fetchExistingMemberships();
    }, []);

    // Fetch existing memberships to prevent overlap
    const fetchExistingMemberships = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token || !memberId) return;

            const response = await fetch(`${API_BASE_URL}/api/members/${memberId}/memberships`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const memberships = data.data || [];
                setExistingMemberships(memberships);

                // Find the latest active or pending membership end date
                const activeMemberships = memberships.filter((m: any) =>
                    m.status === 'active' || m.status === 'planned'
                );

                if (activeMemberships.length > 0) {
                    const latestEndDate = activeMemberships.reduce((latest: Date | null, m: any) => {
                        if (!m.endDate) return latest;
                        const endDate = new Date(m.endDate);
                        return !latest || endDate > latest ? endDate : latest;
                    }, null as Date | null);

                    if (latestEndDate) {
                        const nextDay = new Date(latestEndDate);
                        nextDay.setDate(nextDay.getDate() + 1);
                        setMinStartDate(nextDay.toISOString().split('T')[0]);
                        setStartDate(nextDay.toISOString().split('T')[0]);
                    } else {
                        setMinStartDate(new Date().toISOString().split('T')[0]);
                    }
                } else {
                    setMinStartDate(new Date().toISOString().split('T')[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching existing memberships:', error);
            setMinStartDate(new Date().toISOString().split('T')[0]);
        }
    };

    // Auto-calculate end date when start date or plan changes
    useEffect(() => {
        if (selectedPlan && selectedPlan.durationMonths && startDate) {
            const start = new Date(startDate);
            const end = new Date(start);
            end.setMonth(end.getMonth() + selectedPlan.durationMonths);
            setEndDate(end.toISOString().split('T')[0]);
        }
    }, [selectedPlan, startDate]);

    // Sync dueDate default whenever startDate changes (only if user hasn't manually set a due date)
    useEffect(() => {
        setDueDate(startDate);
    }, [startDate]);

    const fetchPlans = async () => {
        try {
            setLoadingPlans(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return;

            const response = await fetch(`${API_BASE_URL}/api/member-plans`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const allPlans = data.data || [];
                const activePlans = allPlans.filter((plan: Plan) => plan.isActive === true);
                setPlans(activePlans);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load plans');
        } finally {
            setLoadingPlans(false);
        }
    };

    const handleCreateMembership = async () => {
        if (!selectedPlan) return;

        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error('No access token');
            }

            if (mode === 'purchase') {
                const response = await fetch(`${API_BASE_URL}/api/purchases`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        memberId,
                        planId: selectedPlan.id,
                        startDate,
                        endDate: endDate || undefined,
                        discountAmount: discountType === 'amount' ? Number(discountValue) : 0,
                        discountPercent: discountType === 'percent' ? Number(discountValue) : 0
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    setMembershipId(data.data.id);

                    const planPrice = selectedPlan.price || 0;
                    const discountAmt = discountType === 'amount' ? Number(discountValue) : (discountType === 'percent' ? (planPrice * Number(discountValue)) / 100 : 0);
                    const subtotal = planPrice - discountAmt;
                    const taxAmount = subtotal * 0.18;
                    const totalAmount = subtotal + taxAmount;

                    setPaymentSummary({
                        baseAmount: planPrice,
                        discountAmount: discountAmt,
                        discountPercent: discountType === 'percent' ? Number(discountValue) : 0,
                        taxPercent: 18,
                        taxAmount: taxAmount,
                        totalAmount: totalAmount
                    });

                    setCurrentStep('payment-summary');
                } else {
                    const error = await response.json();
                    toast.error(error.error || 'Failed to create purchase');
                }
            } else {
                const response = await fetch(`${API_BASE_URL}/api/members/${memberId}/memberships`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        planId: selectedPlan.id,
                        startDate,
                        endDate: endDate || undefined
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    setMembershipId(data.data.membership.id);
                    setPaymentSummary(data.data.paymentSummary);
                    setCurrentStep('payment-summary');
                } else {
                    const error = await response.json();
                    toast.error(error.error || 'Failed to create membership');
                }
            }
        } catch (error) {
            console.error('Error creating membership:', error);
            toast.error('Failed to create membership');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotal = () => {
        if (!paymentSummary) return { subtotal: 0, discount: 0, tax: 0, total: 0 };
        const baseAmount = Number(paymentSummary.baseAmount) || 0;
        let discountAmount = 0;
        if (discountType === 'amount') {
            discountAmount = Number(discountValue) || 0;
        } else if (discountType === 'percent') {
            const percent = Number(discountValue) || 0;
            discountAmount = (baseAmount * percent) / 100;
        }
        const subtotal = baseAmount - discountAmount;
        const tax = (subtotal * Number(taxPercent)) / 100;
        const total = subtotal + tax;
        return { subtotal: baseAmount, discount: discountAmount, tax, total };
    };

    const handleRecordPayment = async () => {
        if (!membershipId || !paymentMethod) {
            toast.error('Please select a payment method');
            return;
        }

        const paidNowNum = paidNow !== '' ? Number(paidNow) : totals.total;
        if (paidNowNum < 0 || paidNowNum > totals.total) {
            toast.error('Paid now amount must be between 0 and the total amount');
            return;
        }

        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) throw new Error('No access token');

            const payload: any = {
                paymentMethod,
                paymentDate,
                taxPercent: Number(taxPercent),
                paymentNotes: paymentNotes || undefined,
                paidNow: paidNowNum,
                dueDate: paidNowNum < totals.total ? dueDate || undefined : undefined
            };
            if (discountType === 'amount') payload.discountAmount = Number(discountValue);
            else if (discountType === 'percent') payload.discountPercent = Number(discountValue);

            const response = await fetch(`${API_BASE_URL}/api/members/${memberId}/memberships/${membershipId}/payment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                const pendingAmt = totals.total - paidNowNum;
                let status = 'paid';
                if (paidNowNum <= 0) status = 'due';
                else if (paidNowNum < totals.total) status = 'partial';
                setFinalPaymentStatus(status);
                setFinalPendingAmount(pendingAmt);
                setCurrentStep('confirmation');
                const successMessage = data.message || (mode === 'purchase' ? 'Purchase completed successfully!' : 'Payment recorded successfully!');
                toast.success(successMessage);
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to record payment');
            }
        } catch (error) {
            console.error('Error recording payment:', error);
            toast.error('Failed to record payment');
        } finally {
            setLoading(false);
        }
    };

    const totals = calculateTotal();

    const paidNowNum = paidNow !== '' ? Number(paidNow) : totals.total;
    const pendingAmount = Math.max(0, totals.total - paidNowNum);
    const isPartialPayment = paidNow !== '' && paidNowNum < totals.total;

    return (
        <div className="flex flex-col h-full bg-white relative max-h-full overflow-hidden">
            {/* 1. Steps Header (Fixed) */}
            <div className="shrink-0 p-6 bg-white min-h-0 border-b border-gray-50">
                <div className="relative mb-2 px-1">
                    <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-100 -z-10"></div>
                    <div className="flex items-center justify-between">
                        {[
                            { id: 'plan', label: 'Plan', icon: <Calculator className="w-4 h-4" /> },
                            { id: 'dates', label: 'Dates', icon: <Calendar className="w-4 h-4" /> },
                            { id: 'payment-summary', label: 'Summary', icon: <History className="w-4 h-4" /> },
                            { id: 'payment-method', label: 'Payment', icon: <div className="text-sm">💳</div> },
                            { id: 'confirmation', label: 'Success', icon: <div className="text-sm">✨</div> }
                        ].map((step) => {
                            const stepOrder = ['plan', 'dates', 'payment-summary', 'payment-method', 'confirmation'];
                            const currentIndex = stepOrder.indexOf(currentStep);
                            const stepIndex = stepOrder.indexOf(step.id as WizardStep);
                            const isActive = stepIndex === currentIndex;
                            const isCompleted = stepIndex < currentIndex;

                            return (
                                <div key={step.id} className="flex flex-col items-center gap-1.5 flex-1 relative">
                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300 ${isActive
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-100 ring-2 ring-blue-50 scale-110'
                                        : isCompleted
                                            ? 'bg-green-500 text-white shadow-lg shadow-green-50'
                                            : 'bg-white text-gray-400 border border-gray-100 shadow-sm'
                                        }`}>
                                        {isCompleted ? <div className="text-xs font-black">✓</div> : step.icon}
                                    </div>
                                    <span className={`text-[8px] font-black uppercase tracking-widest ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* 2. Middle Content (Scrollable) */}
            <div className="flex-1 overflow-y-auto min-h-0 p-6 custom-scrollbar bg-gray-50/10">
                {currentStep === 'plan' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="mb-4">
                            <h3 className="text-xl font-black text-gray-900 tracking-tight">Select Plan</h3>
                            <p className="text-xs text-gray-500">Choose the best fit for the member.</p>
                        </div>

                        {loadingPlans ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[1, 2, 3, 4, 5, 6].map(i => (
                                    <div key={i} className="h-24 bg-gray-50 rounded-2xl animate-pulse border border-gray-100"></div>
                                ))}
                            </div>
                        ) : plans.length === 0 ? (
                            <div className="bg-amber-50 rounded-2xl p-6 text-center border border-amber-100 italic">
                                <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                                <h4 className="text-sm font-black text-amber-900 mb-1 tracking-tight">No Active Plans</h4>
                                <p className="text-[10px] text-amber-700 max-w-xs mx-auto mb-4">You need at least one active plan in settings.</p>
                                <button
                                    onClick={() => { onCancel(); navigate('/plans'); }}
                                    className="px-4 py-2 bg-amber-600 text-white rounded-lg font-black text-[9px] uppercase tracking-widest hover:bg-amber-700 transition-all"
                                >
                                    Go to Settings
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {plans.map(plan => (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedPlan(plan)}
                                        className={`group p-3 rounded-2xl cursor-pointer transition-all duration-300 border-2 relative overflow-hidden ${selectedPlan?.id === plan.id
                                            ? 'border-blue-600 bg-white shadow-lg shadow-blue-50 ring-2 ring-blue-50'
                                            : 'border-gray-100 bg-white hover:border-blue-200'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between relative z-10">
                                            <div className="space-y-0.5">
                                                <h4 className="text-sm font-black text-gray-900 leading-tight">{plan.name}</h4>
                                                <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest">{plan.durationMonths} Months</p>
                                            </div>
                                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedPlan?.id === plan.id ? 'border-blue-600 bg-blue-600' : 'border-gray-200'}`}>
                                                {selectedPlan?.id === plan.id && <div className="text-white text-[8px] font-black">✓</div>}
                                            </div>
                                        </div>
                                        <div className="mt-2 text-base font-black text-blue-600">₹{plan.price?.toLocaleString()}</div>
                                        <div className={`absolute -bottom-2 -right-2 w-16 h-16 rounded-full opacity-[0.03] ${selectedPlan?.id === plan.id ? 'bg-blue-600 scale-150' : 'bg-gray-900'}`}></div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {currentStep === 'dates' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-blue-600 rounded-3xl p-4 text-white flex items-center justify-between relative overflow-hidden">
                            <div className="relative z-10">
                                <h3 className="text-xl font-black">{selectedPlan?.name}</h3>
                                <p className="text-xs text-blue-100/80 font-bold">₹{selectedPlan?.price?.toLocaleString()} for {selectedPlan?.durationMonths} Months</p>
                            </div>
                            <Calendar className="w-16 h-16 absolute -right-4 -bottom-4 opacity-10" />
                        </div>

                        {existingMemberships.some((m: any) => m.status === 'active' || m.status === 'planned') && (
                            <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100 flex gap-3 lowercase">
                                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-[10px] text-amber-800 font-medium">membership overlap detected. we've auto-adjusted the start date.</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    min={minStartDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl font-bold text-sm focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:border-blue-500 transition-all"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    disabled={!!selectedPlan?.durationMonths}
                                    className="w-full px-4 py-3 bg-gray-100 border border-transparent rounded-xl font-bold text-sm text-gray-500"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 'payment-summary' && paymentSummary && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h3 className="text-lg font-black text-gray-900 tracking-tight">Summary</h3>
                        <div className="bg-white rounded-2xl p-6 space-y-4 border border-gray-100 shadow-sm">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Plan Base</span>
                                <span className="font-black text-gray-900">₹{paymentSummary.baseAmount}</span>
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                        <input type="checkbox" checked={discountType !== 'none'} onChange={(e) => setDiscountType(e.target.checked ? 'percent' : 'none')} className="rounded border-gray-300" />
                                        Discount
                                    </label>
                                    {discountType !== 'none' && (
                                        <div className="flex gap-2">
                                            <select value={discountType} onChange={(e) => setDiscountType(e.target.value as any)} className="text-[10px] font-black p-1 border rounded bg-white">
                                                <option value="percent">%</option>
                                                <option value="amount">₹</option>
                                            </select>
                                            <input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="w-16 text-[10px] font-black p-1 border rounded" />
                                        </div>
                                    )}
                                </div>
                                {totals.discount > 0 && (
                                    <div className="flex justify-between text-emerald-600 font-black text-sm">
                                        <span>Discount Applied</span>
                                        <span>-₹{totals.discount.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-gray-100 pt-4 flex justify-between items-center">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">GST %</span>
                                <input
                                    type="number"
                                    value={taxPercent}
                                    onChange={(e) => setTaxPercent(e.target.value)}
                                    className="w-16 text-[10px] font-black p-1 border rounded bg-white"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                />
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tax (₹)</span>
                                <span className="font-black text-gray-900 text-sm">₹{totals.tax.toFixed(2)}</span>
                            </div>

                            <div className="border-t-2 border-blue-100 pt-4 flex justify-between items-center">
                                <span className="text-sm font-black text-blue-900 uppercase tracking-widest">Total</span>
                                <span className="text-2xl font-black text-blue-600">₹{totals.total.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 'payment-method' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-blue-600 rounded-2xl p-4 text-white flex justify-between items-center">
                            <span className="text-xs font-black uppercase tracking-widest opacity-80">Payable Amount</span>
                            <span className="text-2xl font-black">₹{totals.total.toFixed(2)}</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Method</label>
                                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl font-bold text-sm">
                                    <option value="">Select Method</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="Transfer">Transfer</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Date</label>
                                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl font-bold text-sm" />
                            </div>
                        </div>

                        {/* Partial Payment Section */}
                        <div className="border border-gray-100 rounded-2xl p-4 bg-white space-y-4">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Payment Amount</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Paid Now (₹)</label>
                                    <input
                                        type="number"
                                        value={paidNow}
                                        onChange={(e) => setPaidNow(e.target.value)}
                                        min="0"
                                        max={totals.total}
                                        step="0.01"
                                        className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl font-bold text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                    />
                                    <p className="text-[9px] text-gray-400 ml-1">Enter 0 or partial amount to add a due balance</p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                                        Due Date
                                        {!isPartialPayment && <span className="text-gray-300 font-medium normal-case tracking-normal ml-1">(if paying partial)</span>}
                                    </label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        disabled={!isPartialPayment}
                                        className={`w-full px-4 py-3 border rounded-xl font-bold text-sm transition-all ${isPartialPayment
                                            ? 'bg-white border-amber-200 focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500'
                                            : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'}`}
                                    />
                                </div>
                            </div>
                            {isPartialPayment && (
                                <div className="flex items-center justify-between bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
                                    <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Pending Amount</span>
                                    <span className="text-lg font-black text-amber-600">₹{pendingAmount.toFixed(2)}</span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Notes</label>
                            <textarea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl font-bold text-sm h-20 resize-none" placeholder="Optional notes..."></textarea>
                        </div>
                    </div>
                )}

                {currentStep === 'confirmation' && (
                    <div className="text-center py-6 animate-in zoom-in-95 duration-500">
                        <div className={`w-20 h-20 ${finalPaymentStatus === 'paid' ? 'bg-green-500' : 'bg-amber-500'} text-white rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl ${finalPaymentStatus === 'paid' ? 'shadow-green-100 animate-bounce' : 'shadow-amber-100'}`}>
                            {finalPaymentStatus === 'paid'
                                ? <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                                : <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            }
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-2">
                            {finalPaymentStatus === 'paid' ? 'Success!' : finalPaymentStatus === 'partial' ? 'Partial Payment Recorded' : 'Membership Added'}
                        </h3>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-6">
                            {finalPaymentStatus === 'paid' ? 'Membership Activated' : finalPaymentStatus === 'partial' ? 'Payment Due Balance Remaining' : 'Payment Due'}
                        </p>

                        <div className="max-w-xs mx-auto bg-white rounded-2xl p-5 border border-gray-100 space-y-3 text-left shadow-sm">
                            <div className="flex justify-between">
                                <span className="text-[9px] font-black text-gray-400 uppercase">Total</span>
                                <span className="text-sm font-black text-gray-900">₹{totals.total.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[9px] font-black text-gray-400 uppercase">Paid Now</span>
                                <span className="text-sm font-black text-blue-600">₹{(paidNow !== '' ? Number(paidNow) : totals.total).toLocaleString()}</span>
                            </div>
                            {finalPaymentStatus !== 'paid' && (
                                <div className="flex justify-between">
                                    <span className="text-[9px] font-black text-gray-400 uppercase">Pending</span>
                                    <span className="text-sm font-black text-amber-600">₹{finalPendingAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between"><span className="text-[9px] font-black text-gray-400 uppercase">Method</span><span className="text-sm font-black text-gray-900">{paymentMethod}</span></div>
                            <div className="pt-3 border-t border-gray-100 flex flex-col gap-1">
                                <span className="text-[9px] font-black text-gray-400 uppercase">Validity</span>
                                <span className="text-[10px] font-bold text-gray-900">{new Date(startDate).toLocaleDateString()} — {new Date(endDate).toLocaleDateString()}</span>
                            </div>
                        </div>

                        <button onClick={onSuccess} className="w-full mt-8 py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl">Done</button>
                    </div>
                )}
            </div>

            {/* 3. Footer Navigation (Fixed) */}
            {currentStep !== 'confirmation' && (
                <div className="shrink-0 p-4 border-t border-gray-100 flex gap-3 bg-white shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.08)] z-50">
                    <button
                        onClick={() => {
                            if (currentStep === 'plan') onCancel();
                            else if (currentStep === 'dates') setCurrentStep('plan');
                            else if (currentStep === 'payment-summary') setCurrentStep('dates');
                            else if (currentStep === 'payment-method') {
                                setPaidNow('');  // reset so it re-initializes from total on next entry
                                setCurrentStep('payment-summary');
                            }
                        }}
                        className="flex-1 py-3 bg-white border border-gray-200 text-gray-500 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95"
                    >
                        {currentStep === 'plan' ? 'Cancel' : 'Back'}
                    </button>
                    <button
                        onClick={() => {
                            if (currentStep === 'plan') setCurrentStep('dates');
                            else if (currentStep === 'dates') handleCreateMembership();
                            else if (currentStep === 'payment-summary') {
                                // Pre-fill paidNow with the current total when entering payment step
                                const currentTotal = calculateTotal().total;
                                setPaidNow(currentTotal > 0 ? currentTotal.toFixed(2) : '');
                                setCurrentStep('payment-method');
                            }
                            else if (currentStep === 'payment-method') handleRecordPayment();
                        }}
                        disabled={loading || (currentStep === 'plan' && !selectedPlan) || (currentStep === 'payment-method' && !paymentMethod)}
                        className="flex-[2] py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {loading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                        <span>{loading ? 'Wait...' : (currentStep === 'plan' ? 'Dates' : currentStep === 'dates' ? 'Summary' : currentStep === 'payment-summary' ? 'Payment' : 'Complete')}</span>
                        {!loading && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>}
                    </button>
                </div>
            )}
        </div>
    );
}
